import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import type { RelationType } from '@/types'
import { ContextMenu, type ContextMenuItem } from './ContextMenu'
import { Dialog } from '@/components/ui/Dialog'
import { Button } from '@/components/ui/Button'
import { useTableEditorModal } from '@/hooks/useTableEditorModal'
import { morphToast } from '@/components/ui/Toast'
import { useDiagramStore } from '@/stores'
import { useSelectionStore } from '@/stores/selectionStore'
import { useTabViewStore } from '@/stores/tabViewStore'
import { useClipboardStore } from '@/stores/clipboardStore'

interface TableContextMenuProps {
  open: boolean
  x: number
  y: number
  tableId: string
  onClose: () => void
  onStartRelationship: (tableId: string, type?: RelationType) => void
  readOnly?: boolean
}

export function TableContextMenu({
  open,
  x,
  y,
  tableId,
  onClose,
  onStartRelationship,
  readOnly = false,
}: TableContextMenuProps) {
  const { t } = useTranslation()
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false)
  const table = useDiagramStore((state) => state.tables.find((entry) => entry.id === tableId) ?? null)
  const rels = useDiagramStore((state) => state.rels)
  const deleteTable = useDiagramStore((state) => state.deleteTable)
  const openEdit = useTableEditorModal((state) => state.openEdit)
  const copyTables = useClipboardStore((state) => state.copyTables)
  const hideTables = useTabViewStore((state) => state.hideTables)
  const showTables = useTabViewStore((state) => state.showTables)
  const hiddenTableIds = useTabViewStore((state) => state.hiddenTableIds)

  const hiddenRelatedTableIds = useMemo(() => {
    const hiddenSet = new Set(hiddenTableIds)
    const relatedIds = rels
      .filter((r) => r.from === tableId || r.to === tableId)
      .map((r) => (r.from === tableId ? r.to : r.from))
    return [...new Set(relatedIds)].filter((id) => hiddenSet.has(id))
  }, [rels, tableId, hiddenTableIds])

  const items = useMemo<ContextMenuItem[]>(() => [
    ...(!readOnly ? [{
      id: 'table-edit',
      label: t('contextMenu.editTable'),
      icon: '✎',
      onSelect: () => {
        openEdit(tableId)
      },
    } as ContextMenuItem,
    {
      id: 'table-copy',
      label: t('contextMenu.copyTable'),
      icon: '⎘',
      shortcut: 'Ctrl+C',
      onSelect: () => {
        copyTables([tableId])
        morphToast.success(t('prompts.copyTableDone', { name: tableId }))
      },
    } as ContextMenuItem] : []),
    {
      id: 'table-hide',
      label: t('contextMenu.hideTable'),
      icon: '◫',
      onSelect: () => {
        hideTables([tableId])
        useSelectionStore.getState().clearSelection()
      },
    },
    {
      id: 'table-show-related',
      label: t('contextMenu.showRelatedTables'),
      icon: '◨',
      disabled: hiddenRelatedTableIds.length === 0,
      onSelect: () => {
        showTables(hiddenRelatedTableIds)
      },
    },
    ...(!readOnly ? [{
      id: 'table-delete',
      label: t('contextMenu.deleteTable'),
      icon: '🗑',
      danger: true,
      onSelect: () => {
        setConfirmDeleteOpen(true)
      },
    } as ContextMenuItem,
    {
      type: 'separator',
      id: 'table-separator-rel',
    } as ContextMenuItem,
    {
      id: 'table-rel-start',
      label: t('contextMenu.startRelationship'),
      icon: '⇢',
      onSelect: () => {
        onStartRelationship(tableId)
      },
    } as ContextMenuItem] : []),
  ], [copyTables, hideTables, hiddenRelatedTableIds, onStartRelationship, openEdit, readOnly, showTables, t, tableId])

  if (!table) {
    return null
  }

  return (
    <>
      <ContextMenu
        open={open}
        x={x}
        y={y}
        items={items}
        onClose={onClose}
      />

      <Dialog
        open={confirmDeleteOpen}
        title={t('prompts.deleteTableTitle')}
        onClose={() => setConfirmDeleteOpen(false)}
        onConfirm={() => {
          deleteTable(tableId)
          setConfirmDeleteOpen(false)
          morphToast.success(t('prompts.tableDeleted', { name: tableId }))
        }}
        footer={(
          <>
            <Button variant="ghost" onClick={() => setConfirmDeleteOpen(false)}>
              {t('common.cancel')}
            </Button>
            <Button
              variant="danger"
              onClick={() => {
                deleteTable(tableId)
                setConfirmDeleteOpen(false)
                morphToast.success(t('prompts.tableDeleted', { name: tableId }))
              }}
            >
              {t('common.confirm')}
            </Button>
          </>
        )}
      >
        {t('contextMenu.deleteTableConfirm', { name: tableId })}
      </Dialog>
    </>
  )
}
